import type {
  ContentfulLinkAction,
  ContentfulMediaImage,
} from "@scott/contentful/types";
import type { TeaserItem } from "@/scott/ui/blocks/teaser-section/teaser-section";
import { adaptButton } from "@/services/contentful/assemblies/page/adapter/button-adapter";
import {
  extractCloudinaryAssetUrl,
  getCloudinarySrcSet,
  resolveTransform,
} from "@/services/contentful/assemblies/page/adapter/utils";

export interface TeaserItemInput {
  __typename: "TeaserItem";
  sys?: { id: string };
  internalName?: string | null;
  image?: ContentfulMediaImage | null;
  link?: ContentfulLinkAction | null;
}

type TeaserItemAdapterOptions = {
  /** The active market's `/{country}/{lang}`, for internal page links. */
  basePath?: string;
  index: number;
  parentLabel: string;
  transform: string | string[];
};

/** Maps the shared Contentful Teaser Item into the UI teaser contract. */
export function adaptTeaserItem(
  item: TeaserItemInput,
  options: TeaserItemAdapterOptions
): TeaserItem {
  const itemId = item.sys?.id ?? item.internalName ?? `item-${options.index}`;

  if (!item.image) {
    throw new Error(
      `${options.parentLabel} item "${itemId}" is missing its required image.`
    );
  }

  if (!item.link) {
    throw new Error(
      `${options.parentLabel} item "${itemId}" is missing its required link.`
    );
  }

  const source = extractCloudinaryAssetUrl(item.image.image);
  if (!source) {
    throw new Error(
      `${options.parentLabel} item "${itemId}" has no usable image.`
    );
  }

  const button = adaptButton(item.link, options.basePath);
  if (!button.href.trim()) {
    throw new Error(
      `${options.parentLabel} item "${itemId}" has no usable link.`
    );
  }

  const responsive = getCloudinarySrcSet(source, {
    transform: resolveTransform(options.transform, item.image.hasFocalPoint),
  });

  return {
    id: itemId,
    sys: item.sys,
    label: button.label,
    href: button.href,
    target: button.target,
    image: {
      sys: item.image.sys,
      src: responsive?.src ?? source,
      srcSet: responsive?.srcSet,
      alt: item.image.alternativeText ?? button.label,
    },
  };
}
